import { type FieldApiClient } from "@njbsoft/api-client";
import { type EnsureFreshOutcome, type TransportAdapter } from "./transport-contract";
import { type ChangeRequestSetCreate, type FormSubmissionCreate } from "./types";

// The field-stack protocol client: each TransportAdapter method is the field
// arm of the sync engine's wire surface, bound to the injected typed client.
// Every network call goes through that client, so the shared RefreshManager
// 401-dedup applies.

export interface FieldTransportOptions {
  /**
   * A typed client for the /api/v1/field stack, constructed by the host and
   * handed in — this package never constructs a client of its own.
   */
  client: FieldApiClient;
  /**
   * Injected rather than derived because token expiry is session-layer
   * knowledge (the session store holds the expiry) and this package ships no
   * session policy.
   */
  ensureFresh: () => Promise<EnsureFreshOutcome>;
}

export function createFieldTransport(options: FieldTransportOptions): TransportAdapter {
  const { client, ensureFresh } = options;
  return {
    ensureFresh,

    async createAssignment() {
      // A guard, not a wire call: this transport binds the field stack only,
      // walk-up creation is org-mode-only, and nothing enqueues an assignment
      // row against the field stack — so the engine's assignment phase drains
      // an empty queue here by construction.
      throw new Error(
        "field transport cannot create walk-up assignments: walk-up creation is org-mode-only and nothing enqueues an assignment row on the field stack",
      );
    },

    async patchTransition(t) {
      if (t.toStatus !== "in_progress") {
        // The field wire accepts only the in_progress transition and the
        // queue is fed solely by execution start — an unsendable row is
        // dropped and the next pull restores server truth.
        return "unsendable";
      }
      try {
        const { response } = await client.PATCH("/work-assignments/{assignmentId}", {
          params: { path: { assignmentId: t.assignmentId } },
          body: { status: t.toStatus },
        });
        return response.status;
      } catch {
        return "network";
      }
    },

    async createSubmission(row) {
      // payload_json was written as JSON.stringify of a wire FormSubmissionCreate
      // at enqueue; parsing restores that shape. The deterministic-error envelope
      // (the client's `error`) is captured so decidePostOutcome can name WHY.
      const body = JSON.parse(row.payloadJson) as FormSubmissionCreate;
      try {
        const { data, error, response } = await client.POST("/form-submissions", { body });
        return { status: response.status, data, error };
      } catch {
        return { status: "network", data: undefined, error: undefined };
      }
    },

    async uploadAttachment(_stack, _orgId, submissionId, attachment, evidence) {
      const formData = new FormData();
      // One append line serves both platforms: a React Native descriptor part
      // carries its own filename and the third argument is ignored; a browser
      // Blob part takes its filename from the third argument. The DOM lib
      // types append as Blob-only, so the descriptor arm is widened past it —
      // exactly the platform split EvidencePart exists to carry.
      formData.append("file", evidence.part as Blob, evidence.filename);
      const path = { submissionId, clientAttachmentId: attachment.clientAttachmentId };
      try {
        const { data, response } = await client.POST(
          "/form-submissions/{submissionId}/attachments/{clientAttachmentId}",
          {
            params: { path },
            // The client types multipart bodies as the schema object; the
            // FormData passes through the identity serializer untouched.
            body: formData as never,
            bodySerializer: (b: unknown) => b as FormData,
          },
        );
        return { status: response.status, data };
      } catch {
        return { status: "network", data: undefined };
      }
    },

    async createCompanySubmission(row) {
      // responses_json was written as JSON.stringify of the answer document at
      // enqueue; parsing restores the wire shape. The body carries the parent's
      // CLIENT uuid verbatim — the server resolves the parent form_submission FK
      // from it; no resolved server id is ever sent.
      const responses = JSON.parse(row.responsesJson) as Record<string, unknown>;
      try {
        const { response } = await client.POST("/company-form-submissions", {
          body: {
            client_uuid: row.clientSubmissionId,
            company_id: row.companyId,
            parent_client_submission_id: row.parentClientSubmissionId,
            template_version_id: row.templateVersionId,
            responses,
          },
        });
        return { status: response.status };
      } catch {
        return { status: "network" };
      }
    },

    async getBundle(scope) {
      if (scope.mode !== "field") {
        // The engine's pull branches on scope.mode AFTER this call and would
        // write the field bundle through the org replace arm — a cross-org
        // snapshot laundered into one org's slot. Refuse loudly rather than
        // serve one scope's data under another's request.
        throw new Error(
          "field transport pulls only the field bundle: an org-scope pull would write a cross-org snapshot through the org replace arm",
        );
      }
      try {
        const { data, response } = await client.GET("/sync/fieldwork-bundle");
        return { status: response.status, data };
      } catch {
        return { status: "network", data: undefined };
      }
    },

    async createChangeRequestSet(row) {
      // body_json was written as JSON.stringify of the wire ChangeRequestSetCreate
      // at enqueue; the refusal envelope is kept so the 422 can name its evidence.
      const body = JSON.parse(row.bodyJson) as ChangeRequestSetCreate;
      try {
        const { data, error, response } = await client.POST("/change-request-sets", { body });
        return { status: response.status, data, error };
      } catch {
        return { status: "network", data: undefined, error: undefined };
      }
    },

    async uploadChangeRequestAttachment(setId, attachment, evidence) {
      const request = attachment.request;
      if (request === null) {
        throw new Error(
          `attachment ${attachment.clientAttachmentId} is a form file: only a change_request_set row carries the set route's metadata`,
        );
      }
      const formData = new FormData();
      formData.append("client_attachment_id", attachment.clientAttachmentId);
      formData.append("kind", request.kind);
      if (request.requestClientId !== null) {
        formData.append("request_client_id", request.requestClientId);
      }
      if (request.stagedTestClientSubmissionId !== null) {
        formData.append("staged_test_client_submission_id", request.stagedTestClientSubmissionId);
      }
      // The same platform split as uploadAttachment's file part.
      formData.append("file", evidence.part as Blob, evidence.filename);
      try {
        const { data, response } = await client.POST("/change-request-sets/{setId}/attachments", {
          params: { path: { setId } },
          body: formData as never,
          bodySerializer: (b: unknown) => b as FormData,
        });
        return { status: response.status, data };
      } catch {
        return { status: "network", data: undefined };
      }
    },

    async checkIntakeCandidates(body) {
      try {
        const { data, response } = await client.POST("/change-request-sets/intake-candidates", {
          body,
        });
        return { status: response.status, data: data?.candidates };
      } catch {
        return { status: "network", data: undefined };
      }
    },

    async getCatalogSnapshot(orgId, etag) {
      try {
        const { data, response } = await client.GET("/catalog/snapshot", {
          params: {
            query: { org_id: orgId },
            ...(etag !== null ? { header: { "If-None-Match": `"${etag}"` } } : {}),
          },
        });
        return { status: response.status, data };
      } catch {
        return { status: "network", data: undefined };
      }
    },
  };
}
